import { queryClient, reactQueryUtils } from "@lib/react-query";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { accountApi } from "./account.api";
import { accountKeys } from "./account.keys";
import type { TAccount } from "./types";

export const useSearchAccountsMutation = () => {
  return useMutation({
    mutationFn: (search: string) => accountApi.searchAccounts(search),
  });
};

export const useAddSeatsMutation = () => {
  return useMutation({
    mutationFn: ({
      seats,
      description,
    }: {
      seats: number;
      description: string;
    }) => accountApi.addSeats(seats, description),
    onSuccess: () => {
      reactQueryUtils.invalidateQueries([...accountKeys.account]);
    },
  });
};

export const useBuySeatsMutation = () => {
  return useMutation({
    mutationFn: (seats: number) => accountApi.buySeats(seats),
    onSuccess: () => {
      reactQueryUtils.invalidateQueries([...accountKeys.account]);
    },
  });
};

export const useHandlePlanPropertiesMutation = () => {
  return useMutation({
    mutationFn: (freeSeats: number) =>
      accountApi.handlePlanProperties(freeSeats),
    onMutate: async (freeSeats: number) => {
      await queryClient.cancelQueries({ queryKey: accountKeys.account });
      const previousAccount = reactQueryUtils.getQueryData<TAccount>([
        ...accountKeys.account,
      ]);

      if (previousAccount) {
        const optimisticAccount = {
          ...previousAccount,
          planProperties: {
            ...previousAccount.planProperties,
            freeSeats,
          },
        };
        reactQueryUtils.setQueryData(
          [...accountKeys.account],
          optimisticAccount,
        );
      }

      return { previousAccount };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousAccount) {
        reactQueryUtils.setQueryData(
          [...accountKeys.account],
          context.previousAccount,
        );
      }
    },
    onSettled: () => {
      reactQueryUtils.invalidateQueries([...accountKeys.account]);
    },
  });
};

export const useEnableCollaborationMutation = () => {
  return useMutation({
    mutationFn: () => accountApi.enableCollaboration(),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: accountKeys.account });
      const previousAccount = reactQueryUtils.getQueryData<TAccount>([
        ...accountKeys.account,
      ]);

      if (previousAccount) {
        const optimisticAccount = {
          ...previousAccount,
          collaborationEnabled: true,
        };
        reactQueryUtils.setQueryData(
          [...accountKeys.account],
          optimisticAccount,
        );
      }

      return { previousAccount };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousAccount) {
        reactQueryUtils.setQueryData(
          [...accountKeys.account],
          context.previousAccount,
        );
      }
    },
    onSettled: () => {
      reactQueryUtils.invalidateQueries([...accountKeys.account]);
    },
  });
};

export const useDisconnectCustomDomainMutation = () => {
  return useMutation({
    mutationFn: () => accountApi.disconnectCustomDomain(),
    onSuccess: () => {
      reactQueryUtils.invalidateQueries([...accountKeys.account]);
    },
  });
};

export const useSendVerificationEmailMutation = () => {
  return useMutation({
    mutationFn: () => accountApi.sendVerificationEmail(),
  });
};

export const useUpdateAccountPropertiesMutation = () => {
  return useMutation({
    mutationFn: ({
      type,
      payload,
      id,
    }: {
      type: string;
      payload: Record<string, unknown>;
      id: string;
    }) => accountApi.updateAccountProperties(type, payload, id),
    onMutate: async ({ payload }) => {
      await queryClient.cancelQueries({ queryKey: accountKeys.account });
      const previousAccount = reactQueryUtils.getQueryData<TAccount>([
        ...accountKeys.account,
      ]);

      if (previousAccount) {
        const optimisticAccount = {
          ...previousAccount,
          ...payload,
        };
        reactQueryUtils.setQueryData(
          [...accountKeys.account],
          optimisticAccount,
        );
      }

      return { previousAccount };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousAccount) {
        reactQueryUtils.setQueryData(
          [...accountKeys.account],
          context.previousAccount,
        );
      }
    },
    onSettled: () => {
      reactQueryUtils.invalidateQueries([...accountKeys.account]);
    },
  });
};

export const useUpdateAccountSettingsMutation = () => {
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      accountApi.updateAccountSettings(payload),
    onMutate: async (payload: Record<string, unknown>) => {
      await queryClient.cancelQueries({ queryKey: accountKeys.account });
      const previousAccount = reactQueryUtils.getQueryData<TAccount>([
        ...accountKeys.account,
      ]);

      if (previousAccount) {
        const optimisticAccount = {
          ...previousAccount,
          ...payload,
        };
        reactQueryUtils.setQueryData(
          [...accountKeys.account],
          optimisticAccount,
        );
      }

      return { previousAccount };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousAccount) {
        reactQueryUtils.setQueryData(
          [...accountKeys.account],
          context.previousAccount,
        );
      }
    },
    onSettled: () => {
      reactQueryUtils.invalidateQueries([...accountKeys.account]);
    },
  });
};

export const useUpdateDefaultThemeMutation = () => {
  return useMutation({
    mutationFn: (id: string) => accountApi.updateDefaultTheme(id),
    onMutate: async (themeId: string) => {
      await queryClient.cancelQueries({ queryKey: accountKeys.account });
      const previousAccount = reactQueryUtils.getQueryData<TAccount>([
        ...accountKeys.account,
      ]);

      if (previousAccount) {
        const optimisticAccount: TAccount = {
          ...previousAccount,
          dsId: themeId || undefined,
        };
        reactQueryUtils.setQueryData(
          [...accountKeys.account],
          optimisticAccount,
        );
      }

      return { previousAccount };
    },
    onSuccess: (_data, themeId) => {
      toast.success(
        themeId
          ? "Theme set as default successfully!"
          : "Default theme setting removed",
      );
    },
    onError: (_error, themeId, context) => {
      if (context?.previousAccount) {
        reactQueryUtils.setQueryData(
          [...accountKeys.account],
          context.previousAccount,
        );
      }
      toast.error(
        themeId
          ? "Failed to set theme as default"
          : "Failed to remove default theme setting",
      );
    },
    onSettled: () => {
      reactQueryUtils.invalidateQueries([...accountKeys.account]);
    },
  });
};

export const useUpdateSelectedFontsMutation = () => {
  return useMutation({
    mutationFn: (fonts: { title: string; value: string }[]) =>
      accountApi.updateSelectedFonts(fonts),
    onMutate: async (fonts: { title: string; value: string }[]) => {
      await queryClient.cancelQueries({ queryKey: accountKeys.account });
      const previousAccount = reactQueryUtils.getQueryData<TAccount>([
        ...accountKeys.account,
      ]);

      if (previousAccount) {
        const optimisticAccount = {
          ...previousAccount,
          fonts: {
            ...previousAccount.fonts,
            selected: fonts[0],
          },
        };
        reactQueryUtils.setQueryData(
          [...accountKeys.account],
          optimisticAccount,
        );
      }

      return { previousAccount };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousAccount) {
        reactQueryUtils.setQueryData(
          [...accountKeys.account],
          context.previousAccount,
        );
      }
    },
    onSettled: () => {
      reactQueryUtils.invalidateQueries([...accountKeys.account]);
    },
  });
};

export const useCancelSubscriptionMutation = () => {
  return useMutation({
    mutationFn: () => accountApi.cancelSubscription(),
    onSuccess: () => {
      reactQueryUtils.invalidateQueries([...accountKeys.account]);
      reactQueryUtils.invalidateQueries([...accountKeys.invoices]);
    },
  });
};

export const useUpdatePlanMutation = () => {
  return useMutation({
    mutationFn: ({ planId, discount }: { planId: string; discount: boolean }) =>
      accountApi.updatePlan(planId, discount),
    onSuccess: () => {
      reactQueryUtils.invalidateQueries([...accountKeys.account]);
      reactQueryUtils.invalidateQueries([...accountKeys.invoices]);
    },
  });
};

export const useUpdateAccountCustomizationMutation = () => {
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      accountApi.updateAccountCustomization(body),
    onMutate: async (customizationData: Record<string, unknown>) => {
      await queryClient.cancelQueries({ queryKey: accountKeys.account });
      const previousAccount = reactQueryUtils.getQueryData<TAccount>([
        ...accountKeys.account,
      ]);

      if (previousAccount) {
        const optimisticAccount = {
          ...previousAccount,
          customization: {
            ...previousAccount.customization,
            ...customizationData,
          },
        };
        reactQueryUtils.setQueryData(
          [...accountKeys.account],
          optimisticAccount,
        );
      }

      return { previousAccount };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousAccount) {
        reactQueryUtils.setQueryData(
          [...accountKeys.account],
          context.previousAccount,
        );
      }
    },
    onSettled: () => {
      reactQueryUtils.invalidateQueries([...accountKeys.account]);
    },
  });
};

export const useUpdateSubscriptionLimitationsMutation = () => {
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      accountApi.updateSubscriptionLimitations(data),
    onSuccess: () => {
      reactQueryUtils.invalidateQueries([...accountKeys.account]);
    },
  });
};

export const useUpgradePlanMutation = () => {
  return useMutation({
    mutationFn: (planId: string) => accountApi.upgradePlan(planId),
    onSuccess: () => {
      reactQueryUtils.invalidateQueries([...accountKeys.account]);
      reactQueryUtils.invalidateQueries([...accountKeys.invoices]);
    },
  });
};

export const useTrackEventMutation = () => {
  return useMutation({
    mutationFn: ({
      event,
      properties,
    }: {
      event: string;
      properties: Record<string, unknown>;
    }) => accountApi.trackEvent(event, properties),
  });
};

export const useUpdateTimestampMutation = () => {
  return useMutation({
    mutationFn: (key: string) => accountApi.updateTimestamp(key),
    onSuccess: () => {
      reactQueryUtils.invalidateQueries([...accountKeys.account]);
    },
  });
};

export const useUpdateInvitationLinkMutation = () => {
  return useMutation({
    mutationFn: (status: "active" | "inactive") =>
      accountApi.updateInvitationLink(status),
    onMutate: async (status: "active" | "inactive") => {
      await queryClient.cancelQueries({ queryKey: accountKeys.invitationLink });
      await queryClient.cancelQueries({ queryKey: accountKeys.account });

      const previousInvitationLink = reactQueryUtils.getQueryData<TAccount>([
        ...accountKeys.invitationLink,
      ]);
      const previousAccount = reactQueryUtils.getQueryData<TAccount>([
        ...accountKeys.account,
      ]);

      if (previousAccount?.invitationLink) {
        const optimisticAccount = {
          ...previousAccount,
          invitationLink: {
            ...previousAccount.invitationLink,
            status,
          },
        };
        reactQueryUtils.setQueryData(
          [...accountKeys.account],
          optimisticAccount,
        );
      }

      return { previousInvitationLink, previousAccount };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousInvitationLink) {
        reactQueryUtils.setQueryData(
          [...accountKeys.invitationLink],
          context.previousInvitationLink,
        );
      }
      if (context?.previousAccount) {
        reactQueryUtils.setQueryData(
          [...accountKeys.account],
          context.previousAccount,
        );
      }
    },
    onSettled: () => {
      reactQueryUtils.invalidateQueries([...accountKeys.invitationLink]);
      reactQueryUtils.invalidateQueries([...accountKeys.account]);
    },
  });
};

export const useDeleteAccountMutation = () => {
  return useMutation({
    mutationFn: () => accountApi.deleteAccount(),
    onSuccess: () => {
      reactQueryUtils.invalidateQueries([...accountKeys.all]);
    },
  });
};

export const useSetMobileOnboardingStartedMutation = () => {
  return useMutation({
    mutationFn: (payload: {
      doc_type: string;
      subtype: string;
      preview_gif: string;
      story_id: string;
    }) => accountApi.setMobileOnboardingStarted(payload),
    onSuccess: () => {
      reactQueryUtils.invalidateQueries([...accountKeys.account]);
    },
  });
};

export const useSetFirstTimeLoginMutation = () => {
  return useMutation({
    mutationFn: () => accountApi.setFirstTimeLogin(),
    onSuccess: () => {
      reactQueryUtils.invalidateQueries([...accountKeys.account]);
    },
  });
};
